import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { db } from "@healthdesk/shared-services";
import { useStripe } from "@stripe/react-stripe-js";
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  CircularProgress,
  Link,
} from "@mui/material";
import PricingControls from "./PricingControls";
import { LoadingButton } from "@mui/lab";
import UpgradeFeatures from "./UpgradeFeatures";
import { useAuth } from "@healthdesk/shared-hooks";
import {
  getTrialLabelForRole,
  getTrialLengthForRole,
  detectCountry,
} from "@healthdesk/shared-utils";

const Pricing = ({ uid, role, subscription }) => {
  const navigate = useNavigate();
  const _stripe = useStripe();
  const {
    canStartTrial,
    trialExpired,
    detectedCountry,
    user,
  } = useAuth();
  const [trialLoading, setTrialLoading] = useState(null);
  const [prices, setPrices] = useState({});
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState("monthly");
  const [showPricing, setShowPricing] = useState(false);
  const [experimentArm, setExperimentArm] = useState(null);
  const [experimentPrices, setExperimentPrices] = useState(null);
  const trialLengthDays = getTrialLengthForRole(role);
  const _trialLabel = getTrialLabelForRole(role);
  const isFacility = role === "facility";

  // Country detection and experiment arm assignment for patient role
  useEffect(() => {
    if (role !== "patient" || !uid) return;
    let cancelled = false;

    (async () => {
      try {
        let country = detectedCountry;
        if (!country) {
          country = await detectCountry();
          if (country && user?.uid) {
            const { db: appDb } = await import("@healthdesk/shared-services");
            await appDb.collection("users").doc(user.uid).update({ detectedCountry: country });
          }
        }
        if (!country || cancelled) return;

        const assignArm = firebase.functions().httpsCallable("assignExperimentArm");
        const result = await assignArm({ country });
        if (cancelled) return;

        if (result.data?.arm && result.data?.prices) {
          setExperimentArm(result.data.arm);
          setExperimentPrices(result.data.prices);
        }
      } catch (err) {
        console.debug("Experiment arm assignment skipped:", err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [role, uid, detectedCountry, user?.uid]);

  const getPlans = async () => {
    if (!role) return;
    setPlansLoading(true);
    setPlansError(null);
    try {
      const plansSnapshot = await db
        .collection("plans")
        .where("active", "==", true)
        .where("role", "==", role)
        .get();

      const pricePromises = plansSnapshot.docs.map(async (doc) => {
          const productData = doc.data();

          const pricesSnapshot = await db
            .collection("plans")
            .doc(doc.id)
            .collection("prices")
            .where("active", "==", true)
            .get();

          const priceList = {};

          pricesSnapshot.forEach((price) => {
            const priceData = price.data();
            const rawAmount = priceData.unit_amount;
            if (rawAmount == null || typeof rawAmount !== "number") {
              return;
            }

            const roundNumber = String(rawAmount).slice(-2) === "00";
            const formattedAmount = roundNumber
              ? rawAmount / 100
              : (rawAmount / 100).toFixed(2);

            let interval = priceData.interval || "day";
            if (interval === "monthly") interval = "month";
            if (interval === "yearly") interval = "year";

            const newPrice = {
              id: price.id,
              role: productData.role,
              price: `$${formattedAmount}`,
              interval,
              type: priceData.type || "recurring",
            };

            if (priceData.description) {
              const [tier, interval] = priceData.description.split("_");
              if (tier && interval) {
                // Standard format: "medium_monthly"
                if (!priceList[tier]) {
                  priceList[tier] = {};
                }
                priceList[tier][interval] = newPrice;
              } else if (
                priceData.description === "monthly" ||
                priceData.description === "yearly"
              ) {
                // Facility format: just "monthly" or "yearly"
                if (!priceList["facility"]) {
                  priceList["facility"] = {};
                }
                priceList["facility"][priceData.description] = newPrice;
              }
            } else {
              let key =
                priceData.type === "one_time" ? "day" : priceData.interval;
              priceList[key] = newPrice;
            }
          });

          return priceList;
        });

        const allPriceLists = await Promise.all(pricePromises);
        const mergedPrices = allPriceLists.reduce(
          (acc, curr) => ({ ...acc, ...curr }),
          {}
        );

      setPrices(mergedPrices);
    } catch (error) {
      console.error("Error fetching plans:", error);
      setPlansError(error?.message || "Failed to load plans");
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch when we have uid + role so Firestore sees an authenticated request
    if (uid && role) {
      getPlans();
    } else {
      setPlansLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, role]);

  const startTrial = async (planId = null) => {
    setTrialLoading(planId);
    try {
      const startTrialFunction = firebase
        .functions()
        .httpsCallable("startTrial");
      const result = await startTrialFunction();
      if (result.data.success) {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error starting trial:", error);
      alert(`Error starting trial: ${error.message}`);
      setTrialLoading(null);
    }
  };

  return (
    <>
      {trialExpired && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Trial Expired</AlertTitle>
          {isFacility
            ? "Your CareMap Plus trial has ended. Choose a plan below to keep your facility workflows running smoothly."
            : "Your free trial has ended. Choose a plan below to get unlimited access to Medical SuperIntelligence."}
        </Alert>
      )}

      {canStartTrial && !showPricing ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            my: 4,
          }}
        >
          <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold" }}>
            {isFacility
              ? "Try CareMap Plus Free"
              : "Try Medical SuperIntelligence Free"}
          </Typography>
          {isFacility && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 520, mx: "auto" }}
            >
              Coordinate your waiting room, staffing, and patient flow with the
              same tools you keep on CareMap Plus.
            </Typography>
          )}
          {!isFacility && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 500, mx: "auto" }}
            >
              No credit card required. Full access to AI-powered medical
              intelligence for {trialLengthDays} days.
            </Typography>
          )}
          <UpgradeFeatures
            role={role}
            dense
            sx={{
              textAlign: "left",
              maxWidth: 520,
              mx: "auto",
              mb: 3,
              color: "text.secondary",
              "& .MuiListItemIcon-root": { minWidth: 32 },
            }}
          />
          <LoadingButton
            loading={trialLoading === "startTrial"}
            disabled={trialLoading !== null}
            variant="contained"
            size="large"
            onClick={() => startTrial("startTrial")}
            sx={{ px: 5, py: 1.5, fontSize: "1.1rem", mb: 2 }}
          >
            {isFacility
              ? `Start ${trialLengthDays}-Day CareMap Trial`
              : `Start ${trialLengthDays}-Day Free Trial`}
          </LoadingButton>
          <Box sx={{ mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setShowPricing(true)}
              sx={{
                color: "text.secondary",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View subscription plans →
            </Link>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              maxWidth: 520,
              mx: "auto",
              textAlign: "left",
              mb: 3,
              color: "text.secondary",
            }}
          >
            <UpgradeFeatures
              role={role}
              dense
              sx={{
                "& .MuiListItemIcon-root": { minWidth: 32 },
                color: "text.secondary",
              }}
            />
          </Box>

          <Box id="pricing-plans">
            {plansLoading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading plans…
                </Typography>
              </Box>
            ) : plansError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Unable to load plans</AlertTitle>
                {plansError}
                <Box sx={{ mt: 2 }}>
                  <LoadingButton
                    variant="outlined"
                    size="small"
                    onClick={() => getPlans()}
                  >
                    Try again
                  </LoadingButton>
                </Box>
              </Alert>
            ) : Object.keys(prices).length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>No plans available</AlertTitle>
                There are no subscription plans configured for your account type.
                Please contact support or try again later.
                <Box sx={{ mt: 2 }}>
                  <LoadingButton
                    variant="outlined"
                    size="small"
                    onClick={() => getPlans()}
                  >
                    Retry
                  </LoadingButton>
                </Box>
              </Alert>
            ) : (
              <PricingControls
                prices={prices}
                selectedInterval={selectedInterval}
                onIntervalChange={setSelectedInterval}
                canStartTrial={canStartTrial}
                startTrial={startTrial}
                trialLoading={trialLoading}
                subscription={subscription}
                role={role}
                trialExpired={trialExpired}
                experimentPrices={experimentPrices}
                experimentArm={experimentArm}
              />
            )}
          </Box>

          {canStartTrial && showPricing && (
            <Box sx={{ textAlign: "center", mt: 4, mb: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowPricing(false)}
                sx={{
                  color: "text.secondary",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                ← Back to free trial
              </Link>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default Pricing;
